import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  CATEGORY_META,
  DEFAULT_FILTER,
  filterTitles,
  SORTS,
  type FilterState,
  type SortKey,
} from "@/lib/media";
import type { MediaType } from "@/types/media";
import TitleGrid from "@/components/base/TitleGrid";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import FilterBar from "./components/FilterBar";

const PAGE_SIZE = 6;

function isMediaType(value: string | undefined): value is MediaType {
  return value === "movie" || value === "tv";
}

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const valid = isMediaType(type);

  const filter = useMemo<FilterState>(() => {
    const sortParam = searchParams.get("sort");
    const sort: SortKey = SORTS.some((s) => s.key === sortParam) ? (sortParam as SortKey) : "hot";
    return {
      region: searchParams.get("region") ?? DEFAULT_FILTER.region,
      year: searchParams.get("year") ?? DEFAULT_FILTER.year,
      genre: searchParams.get("genre") ?? DEFAULT_FILTER.genre,
      sort,
    };
  }, [searchParams]);

  const meta = valid ? CATEGORY_META[type] : null;

  usePageMeta({
    title: meta ? `${meta.label}资源 - 西瓜影音` : "分类未找到 - 西瓜影音",
    description: meta ? meta.seoDesc : "西瓜影音影视分类页，未找到对应的分类内容。",
    keywords: meta ? `${meta.label},${meta.label}在线观看,西瓜影音${meta.label},高清${meta.label}` : "西瓜影音,影视分类",
  });

  const result = useMemo(() => {
    if (!valid) return { list: [], total: 0 };
    const list = filterTitles(type, filter);
    return { list, total: list.length };
  }, [valid, type, filter]);

  const pageParam = Number(searchParams.get("page") ?? "1");
  const totalPages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, Number.isNaN(pageParam) ? 1 : pageParam), totalPages);
  const pagedList = result.list.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilter = (key: keyof FilterState, value: string) => {
    const next = new URLSearchParams(searchParams);
    const isDefault = (key !== "sort" && value === "全部") || (key === "sort" && value === "hot");
    if (isDefault) next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setSearchParams(next);
  };

  const resetFilter = () => {
    setSearchParams(new URLSearchParams());
  };

  const changePage = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page <= 1) next.delete("page");
    else next.set("page", String(page));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!valid || !meta) {
    return (
      <div className="w-full px-4 md:px-8 py-16 md:py-24">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="你访问的分类页面不存在或已下线，返回首页看看其他精彩内容吧。"
        >
          <Link
            to="/"
            className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            返回首页
          </Link>
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10 animate-fade-in">
      {/* 面包屑 */}
      <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-5" aria-label="面包屑导航">
        <Link to="/" className="hover:text-primary-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-200">{meta.label}</span>
      </nav>

      {/* 分类标题 */}
      <header className="rounded-lg bg-gradient-to-r from-background-900 to-primary-950 border border-background-800 p-6 md:p-8 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-11 h-11 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className={`${type === "movie" ? "ri-film-line" : "ri-tv-2-line"} text-2xl text-background-50`}></i>
          </span>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50">
            {meta.label}资源
          </h1>
        </div>
        <p className="text-sm md:text-base text-foreground-300 leading-relaxed max-w-3xl">{meta.intro}</p>
      </header>

      <FilterBar
        filter={filter}
        resultCount={result.total}
        onChange={updateFilter}
        onReset={resetFilter}
      />

      {pagedList.length > 0 ? (
        <>
          <TitleGrid items={pagedList} />
          <Pagination page={currentPage} totalPages={totalPages} onChange={changePage} />
        </>
      ) : (
        <EmptyState
          icon="ri-search-eye-line"
          title="没有找到匹配的影视作品"
          description="当前筛选条件下暂时没有结果，试试调整地区、年份或类型，看看更多精彩内容。"
          onReset={resetFilter}
          resetLabel="重置筛选"
        />
      )}
    </div>
  );
}